// Typed mapping of the backend ApiEnvelope / detail error shapes.
// Multiple services use different conventions; this normalizer is the single
// place that knows about them.

import { z } from "zod";

export type ApiErrorCode =
  | "unauthorized"
  | "forbidden"
  | "not_found"
  | "conflict"
  | "validation"
  | "rate_limited"
  | "feature_stale"
  | "feature_missing"
  | "service_unavailable"
  | "network"
  | "unknown";

export interface ApiError {
  code: ApiErrorCode;
  message: string;
  status: number;
  detail?: unknown;
}

const envelopeErrorSchema = z.object({
  error: z
    .object({
      code: z.string().optional(),
      message: z.string().optional(),
    })
    .nullable()
    .optional(),
});

const detailSchema = z.object({ detail: z.string() });
const detailObjectSchema = z.object({
  detail: z.union([z.string(), z.array(z.unknown()), z.record(z.unknown())]),
});

function codeFromStatus(status: number): ApiErrorCode {
  if (status === 401) return "unauthorized";
  if (status === 403) return "forbidden";
  if (status === 404) return "not_found";
  if (status === 409) return "conflict";
  if (status === 422 || status === 400) return "validation";
  if (status === 429) return "rate_limited";
  if (status === 503) return "service_unavailable";
  return "unknown";
}

export async function parseErrorResponse(response: Response): Promise<ApiError> {
  const status = response.status;
  let body: unknown = null;
  try {
    body = await response.clone().json();
  } catch {
    try {
      const text = await response.clone().text();
      if (text) body = { detail: text };
    } catch {
      // ignore
    }
  }

  // ApiEnvelope shape (mobile-bff, tenant-service, api-service)
  const env = envelopeErrorSchema.safeParse(body);
  if (env.success && env.data.error) {
    return {
      code: (env.data.error.code as ApiErrorCode | undefined) ?? codeFromStatus(status),
      message: env.data.error.message ?? response.statusText,
      status,
      detail: body,
    };
  }

  // forecast-{rtm,dam} returns `{error, code, timestamp}` at top level.
  const forecastShape = z
    .object({ error: z.string(), code: z.string().optional() })
    .safeParse(body);
  if (forecastShape.success) {
    const code = forecastShape.data.code;
    let mapped: ApiErrorCode = codeFromStatus(status);
    if (code === "features_stale") mapped = "feature_stale";
    if (code === "features_missing" || code === "features_age_unknown") mapped = "feature_missing";
    return { code: mapped, message: forecastShape.data.error, status, detail: body };
  }

  // FastAPI HTTPException `{detail: "..."}`
  const detail = detailSchema.safeParse(body);
  if (detail.success) {
    return { code: codeFromStatus(status), message: detail.data.detail, status, detail: body };
  }
  const detailObj = detailObjectSchema.safeParse(body);
  if (detailObj.success) {
    return {
      code: codeFromStatus(status),
      message: typeof detailObj.data.detail === "string" ? detailObj.data.detail : "Request failed",
      status,
      detail: body,
    };
  }

  return { code: codeFromStatus(status), message: response.statusText || "Request failed", status, detail: body };
}

export function networkError(error: unknown): ApiError {
  return {
    code: "network",
    message: error instanceof Error ? error.message : "Network error",
    status: 0,
    detail: error,
  };
}

export function isApiError(value: unknown): value is ApiError {
  return (
    typeof value === "object" &&
    value !== null &&
    "code" in value &&
    "message" in value &&
    "status" in value
  );
}
